// Runtime accent theming. Tailwind's `primary` palette is wired to CSS variables
// (--p-50 … --p-900, each a space-separated "r g b" triplet). This generates a
// full palette from a single accent hex and writes those variables, so changing
// the accent re-themes every `*-primary-*` class app-wide.

export const DEFAULT_ACCENT = '#ef4444';

// Target lightness (%) per Tailwind shade level — mirrors the default red ramp.
const SHADE_LIGHTNESS: Record<number, number> = {
  50: 97,
  100: 94,
  200: 86,
  300: 77,
  400: 66,
  500: 60,
  600: 50,
  700: 42,
  800: 34,
  900: 27,
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

/**
 * Apply an accent color by generating a 50–900 palette and writing the CSS
 * variables Tailwind's `primary` palette reads. Invalid input is ignored.
 */
export function applyAccentColor(hex: string): void {
  if (typeof document === 'undefined') return;
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const { h, s } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const root = document.documentElement;
  // Keep a little saturation floor so very desaturated picks still read as a hue.
  const sat = clamp(s, 35, 95);
  for (const [level, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    const { r, g, b } = hslToRgb(h, sat, lightness);
    root.style.setProperty(`--p-${level}`, `${r} ${g} ${b}`);
  }
}

/** Remove the inline overrides so the stylesheet defaults (red ramp) apply again. */
export function resetAccentColor(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.keys(SHADE_LIGHTNESS).forEach((level) => root.style.removeProperty(`--p-${level}`));
}

/** Apply when non-default, otherwise restore stylesheet defaults. */
export function syncAccentColor(hex: string): void {
  if (!hex || hex.toLowerCase() === DEFAULT_ACCENT) resetAccentColor();
  else applyAccentColor(hex);
}
